// hooks/useGoalsStats.ts
import { useState, useCallback, useEffect } from "react";
import { goalsAPI, GoalStat, GoalProgress } from "../lib/api/goals";

export function useGoalsStats() {
    const [summaryStats, setSummaryStats] = useState<GoalStat[]>([]);
    const [goalProgress, setGoalProgress] = useState<GoalProgress[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [calculatedStats, setCalculatedStats] = useState<{
        totalGoals: number;
        completedGoals: number;
        pendingGoals: number;
        completionRate: number;
        onTrack: number;
        behind: number;
    } | null>(null);

    // Función para cargar estadísticas de resumen
    const fetchSummaryStats = useCallback(async (timeRange: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month') => {
        setIsLoading(true);
        setError(null);

        try {
            const stats = await goalsAPI.getSummaryStats(timeRange);
            setSummaryStats(stats);
            return stats;
        } catch (err) {
            console.error('Error fetching summary stats:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch summary statistics');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Función para cargar progreso de objetivos específicos
    const fetchGoalProgress = useCallback(async (
        timeRange: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month',
        goalId?: string
    ) => {
        setIsLoading(true);
        setError(null);

        try {
            const progress = await goalsAPI.getProgressStats(timeRange, goalId);

            setGoalProgress(progress);
            return progress;
        } catch (err) {
            console.error('Error fetching goal progress:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch goal progress');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Función para cargar todos los datos del dashboard
    const fetchDashboardData = useCallback(async (
        timeRange: 'day' | 'week' | 'month' = 'month'
    ) => {
        setIsLoading(true);
        setError(null);

        try {
            const dashboardData = await goalsAPI.getDashboardData(timeRange);
            setSummaryStats(dashboardData.summaryStats);
            setGoalProgress(dashboardData.goalProgress);
            setCalculatedStats(dashboardData.calculatedStats);
            return dashboardData;
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Función para actualizar estadísticas (por ejemplo, cuando se completa una tarea)
    const refreshStats = useCallback(async (timeRange: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month') => {
        await Promise.all([
            fetchSummaryStats(timeRange),
            fetchGoalProgress(timeRange)
        ]);
    }, [fetchSummaryStats, fetchGoalProgress]);

    // Función para obtener estadísticas calculadas
    const getCalculatedStats = useCallback(() => {
        if (calculatedStats) return calculatedStats;

        if (summaryStats.length === 0) return null;

        // Calcular stats manualmente si no tenemos los valores calculados
        const totalGoals = summaryStats.find(stat => stat.title === "Total de Objetivos")?.value || 0;
        const completedGoals = summaryStats.find(stat => stat.title === "Objetivos Completados")?.value || 0;
        const onTrack = summaryStats.find(stat => stat.title === "Objetivos al Día")?.value || 0;
        const behind = summaryStats.find(stat => stat.title === "Objetivos Atrasados")?.value || 0;

        return {
            totalGoals,
            completedGoals,
            pendingGoals: totalGoals - completedGoals,
            completionRate: totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0,
            onTrack,
            behind
        };
    }, [summaryStats, calculatedStats]);

    return {
        summaryStats,
        goalProgress,
        isLoading,
        error,
        calculatedStats: getCalculatedStats(),
        fetchSummaryStats,
        fetchGoalProgress,
        fetchDashboardData,
        refreshStats
    };
}